import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { handleOpenWallet } from "api/fetchers";
import OpenWalletInfoModal from "./OpenWalletInfoModal";
import OpenWalletPaymentModal from "./OpenWalletPaymentModal";

const WalletDetailsModal = ({
    isOpen,
    currencyCode,
    onClose,
    onOpenWallet,
    onBeforeOpenWallet,
  autoOpen = false,
  initialStep = 1,
}) => {
    const autoRanRef = useRef(false);
  const [step, setStep] = useState(initialStep);
    const [isLoading, setIsLoading] = useState(false);
    const { business } = useSelector((state) => state);
    const businessId = business?._id;

  const normalizedCode = String(currencyCode || "").toUpperCase();
  const isCrypto = ["USDC", "USDT"].includes(normalizedCode);
  const totalSteps = 2;

  useEffect(() => {
    if (!isOpen) {
      setStep(initialStep);
      autoRanRef.current = false;
    } else {
      setStep(initialStep);
    }
  }, [isOpen, initialStep]);

    useEffect(() => {
        if (!isOpen || !autoOpen || autoRanRef.current) return;
        autoRanRef.current = true;
      Promise.resolve().then(() => executeOpenWallet());
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, autoOpen]);

  if (!isOpen || !currencyCode) return null;

  const handleContinue = () => {
    if (onBeforeOpenWallet?.(currencyCode)) {
      onClose?.();
      return;
    }

    if (totalSteps > 1) {
      setStep(2);
    } else {
      executeOpenWallet();
    }
  };

  const executeOpenWallet = async (paymentAccount = null, extraPaymentData = null) => {
        if (!currencyCode || isLoading) return;
        if (onBeforeOpenWallet?.(currencyCode)) {
            onClose?.();
            return;
        }

        try {
          setIsLoading(true);
          const resp = await handleOpenWallet({
            currency: currencyCode,
            businessId,
            chargeCurrency: paymentAccount?.currency,
            paymentCurrency: paymentAccount?.currency,
          });
            const payload = resp?.data ?? resp;
            if (!payload || resp?.status >= 400) {
                throw new Error(resp?.message || payload?.message || "Failed to open wallet");
            }

            const openedCurrency = String(payload?.data?.currency || currencyCode).toUpperCase();
          const isCryptoWallet = ["USDC", "USDT"].includes(openedCurrency);
            const firstProvider = (payload?.providers || [])[0] || {};

          if (isCryptoWallet) {
                onOpenWallet?.({
                    currency: openedCurrency,
                    walletType: payload?.data?.type || payload?.type || "crypto",
                  address: firstProvider?.address || firstProvider?.depositAddress || "",
                });
                onClose?.();
                toast.success("Wallet opened successfully");
                return;
            }

            const get = (...keys) => {
                for (const k of keys) {
                    const v = k
                        ?.split(".")
                        .reduce(
                            (o, key) => (o && o[key] !== undefined ? o[key] : undefined),
                            payload
                        );
                    if (v !== undefined && v !== null && v !== "") return v;
                    const vp = k
                        ?.split(".")
                        .reduce(
                            (o, key) => (o && o[key] !== undefined ? o[key] : undefined),
                            firstProvider
                        );
                    if (vp !== undefined && vp !== null && vp !== "") return vp;
                }
                return "";
            };

            const accountNumber = get("accountNumber", "iban", "account_no");
            const bankName = get("bankName", "bank", "bank_name") || "Tradevu Wallet";
            const accountName = get("accountName", "account_name") || payload?.businessName || "";
          const sortCode = get("sortCode", "sort_code");
          const routingNumber = get("routingNumber", "routing_number");
          const iban = get("iban");
            const providerStatus = String(firstProvider?.status || "").toLowerCase();
            const isPending = providerStatus === "pending" || !accountNumber;

            if (isPending) {
                onOpenWallet?.({
                    currency: openedCurrency,
                    review: true,
                    providerStatus,
                  accountName,
                  accountNumber,
                  bankName,
                  sortCode,
                  routingNumber,
                  iban,
                });
                onClose?.();
                toast.success(`[${openedCurrency}] wallet request received`);
                return;
            }

            onOpenWallet?.({
                currency: openedCurrency,
                accountName,
                accountNumber,
                bankName,
              sortCode,
              routingNumber,
              iban,
            });
            onClose?.();
            toast.success("Wallet opened successfully");
        } catch (err) {
            toast.error(err?.message || "Failed to open wallet");
        } finally {
            setIsLoading(false);
        }
    };

  if (step === 2) {
    return (
      <OpenWalletPaymentModal
        isOpen={isOpen}
        currencyCode={currencyCode}
        step={2}
        totalSteps={totalSteps}
        isLoading={isLoading}
        onClose={onClose}
        onBack={() => (initialStep === 2 ? onClose?.() : setStep(1))}
        onSubmit={(account) => executeOpenWallet(account)}
      />
    );
  }

  return (
    <OpenWalletInfoModal
      isOpen={isOpen}
      currencyCode={currencyCode}
      step={1}
      totalSteps={totalSteps}
      isLoading={isLoading}
      onClose={onClose}
      onContinue={handleContinue}
      onSubmit={() => {
        if (totalSteps > 1) {
          handleContinue();
        } else {
          executeOpenWallet();
        }
      }}
    />
  );
};

export default WalletDetailsModal;
